import isEqual from "lodash/isEqual";
import React from "react";
import { FLASH_SIGNIN_SUCCESS } from "~/entities/flash/constants";
import { Flash } from "~/entities/flash/types";
import { isValidFlashes } from "~/entities/flash/validators";
import {
  WindowWithENV,
  WindowWithFrontChat,
  WindowWithGTM,
  WindowWithLibraries,
} from "~/entities/global/types";
import { GA4LoginEvent } from "~/entities/gtm/types";
import { User } from "~/entities/user/types";
import { useHydrated } from "~/hooks/useHydrated";
import { ga4ClearPreviousEvent } from "~/utils/gtm";

function recurseInit(
  callback: () => boolean,
  name: string,
  retries: number = 0,
) {
  const isSuccess = callback();

  if (isSuccess) return;

  if (retries > 4) {
    return;
  }

  setTimeout(() => recurseInit(callback, name, retries + 1), 500);
}

type Params = {
  cookieFlash: Record<string, unknown>[] | null;
  user: User | null;
  head: React.RefObject<HTMLHeadElement> | undefined;
  nonce: string;
};

const useStoreHydration = ({ cookieFlash, user, head, nonce }: Params) => {
  const isHydrated = useHydrated();
  const [flash, setFlash] = React.useState<Flash[]>([]);
  const [gtmID, setGTMID] = React.useState<string | undefined>();

  React.useEffect(
    function showFlashFromCookie() {
      setFlash(isValidFlashes(cookieFlash) ? cookieFlash : []);
    },
    [cookieFlash],
  );

  React.useEffect(
    function pushesLoginGTMEvent() {
      if (!gtmID || !cookieFlash) return;

      if (
        user &&
        !user?.doNotSellMyInformation &&
        cookieFlash.some((flash) => isEqual(flash, FLASH_SIGNIN_SUCCESS))
      ) {
        const dataLayer = (window as WindowWithGTM)?.dataLayer;
        const event: GA4LoginEvent = {
          event: "ga4_login",
          ecommerce: {
            method: "Manual",
            customer_type: user.customerType,
          },
        };

        dataLayer?.push(ga4ClearPreviousEvent());
        dataLayer?.push(event);
      }
    },
    [gtmID, cookieFlash, user],
  );

  React.useEffect(
    function initializeFrontChatEffect() {
      if (!isHydrated || user?.doNotSellMyInformation) return;

      let cancelled = false;

      function initFrontChat() {
        if (cancelled) return true;
        const FrontChat = (window as WindowWithFrontChat).FrontChat;
        const ENV = (window as WindowWithENV).ENV;

        if (!FrontChat || !ENV?.FRONTCHAT_ID) {
          return false;
        }

        FrontChat("init", { chatId: ENV.FRONTCHAT_ID });
        return true;
      }

      recurseInit(initFrontChat, "FrontChat");

      return () => {
        cancelled = true;
      };
    },
    [isHydrated],
  );

  React.useEffect(
    function initializeMerchantWidget() {
      if (!isHydrated || user?.doNotSellMyInformation) return;

      let cancelled = false;

      function initMerchantWidget() {
        if (cancelled) return true;
        const merchantwidget = (window as WindowWithLibraries).merchantwidget;

        if (!merchantwidget) return false;

        merchantwidget.start({ position: "LEFT_BOTTOM" });

        return true;
      }

      recurseInit(initMerchantWidget, "MerchantWidget");

      return () => {
        cancelled = true;
      };
    },
    [isHydrated],
  );

  React.useEffect(
    function initializeGTM() {
      const w = window as WindowWithENV;

      if (
        !isHydrated ||
        user?.doNotSellMyInformation ||
        w.ENV?.ENVIRONMENT === "development" ||
        !w.ENV?.GOOGLE_TAG_MANAGER_ID ||
        !head?.current ||
        !nonce ||
        gtmID
      )
        return;

      const gtmScript = document.createElement("script");
      gtmScript.setAttribute("nonce", nonce);
      gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-${w.ENV.GOOGLE_TAG_MANAGER_ID}');`;
      setGTMID(w.ENV.GOOGLE_TAG_MANAGER_ID);
      head.current.appendChild(gtmScript);
    },
    [isHydrated, head?.current, gtmID, nonce],
  );

  React.useEffect(() => {
    if (!user) return;
    sessionStorage.setItem("user", JSON.stringify(user));
  }, [user]);

  return {
    gtmID,
    flash,
  };
};

export default useStoreHydration;
